import Label from 'types/Label';
import { ChecklistSeverityEnum, Checklist } from 'types/Checklist';
import { Position } from 'types/Position';
import GenericQuestion from 'types/Question';
import QuestionForm from 'types/QuestionForm';
import Media, { MediaInterface } from 'types/Media';
import { AbstractMedia } from 'types/AbstractMedia';
import { Policy } from 'modules/auth/types/Policies';
import { Dealer } from 'modules/dealers/types/Dealer';
import { KeyStatus } from 'modules/kiosk/types/Kiosk';
import RestrictedAccess from 'types/RestrictedAccess';
import { KioskPreference } from 'types/KioskPreference';
import { Item } from 'modules/vehicleCheck/types/VehicleCheckModel';
import { PublicSlot } from 'modules/kiosk/types/KeysSafe';
import { CustomerInfo } from 'modules/selfServices/types/CustomerInfo';
import { KioskConnectionStatus } from 'modules/dealers/types/ContextResponse';

import SelfServiceStatus from './SelfServiceStatus';
import { SelfServiceSteps } from 'modules/selfServices/types/SelfServiceSteps';
import { ExternalServiceStatus } from '../constants';

export enum BreakdownServiceAuthorType {
  CUSTOMER = 'CUSTOMER',
  BREAKDOWN_MECHANIC = 'BREAKDOWN_MECHANIC',
}

export enum CustomerType {
  CUSTOMER = 'CUSTOMER',
  ADDITIONAL_CUSTOMER = 'ADDITIONAL_CUSTOMER',
}

export const QUICK_CHECK_IN = 'QUICK_CHECK_IN';

export enum SelfServiceType {
  CHECK_IN = 'CHECK_IN',
  CHECK_OUT = 'CHECK_OUT',
  BREAKDOWN = 'BREAKDOWN',
}

export enum SelfServiceOrigin {
  HOME = 'HOME',
  KIOSK = 'KIOSK',
  ON_SITE = 'ON_SITE',
}

export enum FinalInvoiceStatus {
  CREATED = 'CREATED',
  PAID = 'PAID',
  SENT = 'SENT',
  PRE_AUTHORIZED = 'PRE_AUTHORIZED',
}

export enum FuelLevel {
  FULL = 'FULL',
  THREE_QUARTERS = 'THREE_QUARTERS',
  HALF = 'HALF',
  ONE_QUARTER = 'ONE_QUARTER',
  RESERVE = 'RESERVE',
}

export enum DocumentStatus {
  ACCEPTED = 'ACCEPTED',
  REQUESTED = 'REQUESTED',
  TO_VALIDATE = 'TO_VALIDATE',
  REFUSED = 'REFUSED',
  SKIPPED = 'SKIPPED',
}

export enum SignatureStatus {
  SIGNED = 'SIGNED',
  UNSIGNED = 'UNSIGNED',
}

export enum FlowType {
  CLASSIC = 'CLASSIC',
  QUICK = 'QUICK',
}

export const AllSelfServiceTypes = Object.values(SelfServiceType) as SelfServiceType[];

export type FinalInvoice = {
  id: string;
  payable: boolean;
  authorId: string;
  createdOn: string;
  enabled: boolean;
  name: string;
  link?: string;
  price: number;
  status: FinalInvoiceStatus;
  medias?: AbstractMedia[];
  pending: boolean;
  // The statuses of the last PAYMENT_REQUEST
  failed: boolean;
  aborted: boolean;
};

// Property computed on our side to display easily a repairOrder
interface RepairOrderUtils {
  translatedName?: string,
  translatedDescription?: string,
  vat?: number;
}

export interface RepairOrder extends RepairOrderUtils {
  adviceName: Label;
  price?: number;
  discountedPrice?: number;
  coefficient: number;
  description?: Label;
  payerId?: string;
  workOrderTag?: string;
  severity?: ChecklistSeverityEnum;
}

export interface Maps {
  id: string;
  media: AbstractMedia;
  name: string;
  status: string;
}

interface ParkingMap {
  allowParkingSpot: boolean;
  maps?: Maps[];
  selectedMaps?: Record<string, string>;
}

export interface Parking {
  mapId?: string;
  x?: number;
  y?: number;
  spotNumber?: string;
}

interface Vehicle {
  color?: string;
  description?: string;
  fuelLevel?: FuelLevel;
  medias?: AbstractMedia[];
  mileage?: number;
  parking?: Parking;
  registration?: string;
}

export enum DocumentValidationType {
  MANUAL = 'MANUAL',
  AUTOMATIC = 'AUTOMATIC',
}

export enum AutomatedValidationStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  COMPLETED_FRAUD = 'COMPLETED_FRAUD',
  DOCUMENT_SCAN_UNSUCCESSFUL = 'DOCUMENT_SCAN_UNSUCCESSFUL',
  SELFIE_SCAN_UNSUCCESSFUL = 'SELFIE_SCAN_UNSUCCESSFUL',
}

export enum AutomatedValidationWorkflowType {
  DOCUMENT_ONLY = 'DOCUMENT_ONLY',
  DOCUMENT_AND_IDENTITY = 'DOCUMENT_AND_IDENTITY',
}

type AutomatedDocumentValidation = {
  workflowType: AutomatedValidationWorkflowType;
};

export interface MobilityDocument {
  id: string;
  name: Label;
  medias?: Media[];
  comment?: string;
  accepted: boolean;
  status?: DocumentStatus;
  validationType: DocumentValidationType;
  validationStatus?: AutomatedValidationStatus;
}

interface ContractSignature {
  page: number;
  topLeft: Position;
  bottomRight: Position;
}

export interface BasicContract {
  id: string;
  name?: Label;
  signatureBox?: ContractSignature;
  media?: AbstractMedia;
  status?: SignatureStatus;
}

export interface Mobility {
  id?: string;
  vehicle?: Vehicle;
  parkingReturn?: Parking;
  notPrepared?: boolean;
  selfServiceId?: string;
  contract?: BasicContract;
  documents?: MobilityDocument[];
  customerAccepted?: boolean;
  deliveryDocument?: BasicContract;
  courtesyVehicleCheckEnabled?: boolean;
  automatedDocumentValidation?: AutomatedDocumentValidation;
}

interface VehicleCheck {
  id: string;
  name: string;
  items: Item[];
}

interface SignatureBox {
  bottomRight: Position;
  page: number;
  topLeft: Position;
}

interface WorkOrder {
  media: Media;
  signatureBox: SignatureBox;
  status: SignatureStatus;
}

interface PreAuthorizedAmounts {
  CUSTOMER?: number,
  LEASER?: number,
}

export interface Payer {
  id: string,
  name: Label,
}

export type ConfiguredWorkflows = { [type in SelfServiceType | typeof QUICK_CHECK_IN] : SelfServiceSteps[] };

export type AutomaticDocumentValidationPayload =  {
  selfServiceId: string;
  workflowType: AutomatedValidationWorkflowType
};

export type AutoValidationWorkflow = {
  url: string;
  id: string;
  provider: string;
};

export type AutoValidationQueryResponse = {
  mobility: {
    documents: MobilityDocument[];
  };
  validationStatus?: AutomatedValidationStatus;
};
export type AutoValidationQueryStatusResponse = {
  status: ExternalServiceStatus
};


export type MobilityInstructionsType = {
  message: Record<string, string>;
  medias: AbstractMedia[];
};
type AdditionalCustomer = {
  info?: CustomerInfo;
  validated?: boolean;
};

export interface SelfService {
  id: string;
  autoValidationWorkflow?: AutoValidationWorkflow;
  customerComment?: string;
  repairOrder?: RepairOrder[];
  checklists?: Array<Checklist>;
  proactiveChecklists?: Checklist[];
  customerInfo?: CustomerInfo;
  additionalCustomer?: AdditionalCustomer;
  dealer: Dealer;
  documents?: BasicContract[];
  keyStatus?: KeyStatus;
  questionForm?: QuestionForm;
  medias?: MediaInterface[];
  signature?: Pick<MediaInterface, 'uploadId'>;
  valueAddedTax: number;
  finalInvoice?: FinalInvoice;
  interventionMedias?: AbstractMedia[];
  /** @description `true` when summary signature is enabled AND there was modifications between HOME flow and the current one */
  isSummarySignatureNeeded?: boolean;
  comment?: string;
  parkingMap?: ParkingMap;
  parking?: Parking;
  type: SelfServiceType;
  elapsedTime?: number;
  status: SelfServiceStatus;
  origin: SelfServiceOrigin;
  originContext?: {
    [origin in SelfServiceOrigin]?: {
      elapsedTime?: number;
      status: SelfServiceStatus;
    };
  };
  invoicePaymentMandatory?: boolean;
  slot?: PublicSlot;
  vehicleCheckAccepted?: boolean;
  courtesyVehicleCheckAccepted?: boolean;
  legalNotice?: string;
  mobilityEnabled?: boolean;
  mobility?: Mobility;
  mobilityInstructions?: MobilityInstructionsType;
  payers?: Payer[];
  breakdownServiceEmail?: string;
  vehicleCheck?: VehicleCheck;
  courtesyVehicleCheck?: VehicleCheck;
  workOrder?: WorkOrder;
  kioskPreference?: KioskPreference;
  preAuthorizedAmounts?: PreAuthorizedAmounts,
  kioskConnectionStatus?: KioskConnectionStatus,
  kioskPreferencesQuestions?: Array<GenericQuestion>;
  breakdownServiceAuthorType?: BreakdownServiceAuthorType,
  reopen?: {
    until?: string;
    allowed: boolean;
  },
  isQuick?: boolean,
  isAdditionalCustomerDriving?: boolean,
  registration: string;
  policies?: Array<Policy>;
  kioskRestrictedAccess?: RestrictedAccess;
}

export interface PhoneNumber {
  local: string;
  prefix: string;
}

export const isEqualPhoneNumber = (a: PhoneNumber, b: PhoneNumber) => a?.prefix === b?.prefix && a?.local === b?.local;
